import { router, useLocalSearchParams } from 'expo-router';
import { XIcon } from 'lucide-react-native';
import * as React from 'react';
import { ActivityIndicator, Pressable, useWindowDimensions, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { CheckedInConfirmation } from '@/components/events/checked-in-confirmation';
import { MemberIdentityQr } from '@/components/qr/member-identity-qr';
import { BRAND } from '@sixam/config/brand';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import { memberCheckInMessage } from '@sixam/shared/event-checkin-core';
import { getCurrentTime } from '@/lib/dev-clock';
import { fetchEventCheckInEligibility } from '@/lib/event-checkins';
import { fetchEventById, type Event } from '@/lib/events';
import { fetchMemberQrJwt } from '@/lib/member-qr-client';

// The runner is standing in front of an operator, so the confirmation needs to
// arrive while they are still there — not on the next screen refresh.
const CHECK_IN_POLL_MS = 3000;

/**
 * The runner's side of the start line: their code, big, with the run it belongs
 * to named above it. The app opens straight onto this when their event is close,
 * so being scanned takes zero taps.
 *
 * It watches for the scan rather than waiting to be told — the moment an
 * operator checks them in, the code is replaced by a confirmation, so nobody has
 * to ask "did that work?".
 */
export default function MemberCheckInScreen() {
  const { auto, id } = useLocalSearchParams<{ auto?: string; id: string }>();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const [event, setEvent] = React.useState<Event | null>(null);
  const [jwt, setJwt] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isCheckedIn, setIsCheckedIn] = React.useState(false);

  const qrSize = Math.min(width - 80, 380);

  React.useEffect(() => {
    let active = true;

    Promise.all([fetchEventById(id), fetchMemberQrJwt()])
      .then(([loadedEvent, token]) => {
        if (active) {
          setEvent(loadedEvent);
          setJwt(token.jwt);
        }
      })
      .catch((loadError: unknown) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Could not load your code.');
        }
      });

    return () => {
      active = false;
    };
  }, [id]);

  // Poll until checked in, then stop — there is nothing left to watch for.
  React.useEffect(() => {
    if (isCheckedIn) {
      return undefined;
    }

    let active = true;

    async function check() {
      try {
        const { eligibility } = await fetchEventCheckInEligibility(id);
        if (active && eligibility.actorCheckedIn) {
          setIsCheckedIn(true);
        }
      } catch {
        // A missed poll is not worth reporting; the next one will do.
      }
    }

    void check();
    const timer = setInterval(() => void check(), CHECK_IN_POLL_MS);

    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [id, isCheckedIn]);

  function close() {
    if (router.canGoBack()) {
      router.back();
      return;
    }
    router.replace('/app/home');
  }

  let body: React.ReactNode = <ActivityIndicator />;
  if (error) {
    body = (
      <Text className="text-center text-destructive" selectable>
        {error}
      </Text>
    );
  } else if (isCheckedIn) {
    body = <CheckedInConfirmation eventTitle={event?.title} />;
  } else if (jwt) {
    body = (
      <View className="rounded-md border-2 border-border bg-white p-3">
        <MemberIdentityQr size={qrSize} value={jwt} />
      </View>
    );
  }

  return (
    <View className="flex-1 bg-background">
      <View className="flex-row items-center gap-3 px-4 py-3">
        <View className="min-w-0 flex-1">
          <Text variant="label" className="text-muted-foreground">
            {isCheckedIn ? 'Checked in to' : 'Checking in to'}
          </Text>
          <Text className="text-lg font-bold" numberOfLines={1}>
            {event?.title ?? (error ? 'Event unavailable' : 'Loading…')}
          </Text>
        </View>
        <Pressable
          accessibilityLabel="Close"
          accessibilityRole="button"
          className="size-10 items-center justify-center rounded-full bg-muted active:opacity-70"
          onPress={close}
        >
          <Icon as={XIcon} className="size-6 text-foreground" />
        </Pressable>
      </View>

      {/*
        Only when the app opened itself here, and only until they are scanned —
        after that the green panel says everything worth saying.
      */}
      {auto === '1' && event && !isCheckedIn ? (
        <View
          className="mx-4 mb-1 rounded-md px-4 py-3"
          style={{ backgroundColor: BRAND.color.orange }}
        >
          <Text className="font-bold text-white">
            {memberCheckInMessage(event, getCurrentTime())}
          </Text>
        </View>
      ) : null}

      <View
        className="flex-1 items-center justify-center px-6"
        style={{ paddingBottom: insets.bottom + 16 }}
      >
        {body}
      </View>
    </View>
  );
}
